import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useSelector } from 'react-redux';
import { calculateToolUsagePercentageFromRedux, calculateToolUsagePercentageFromBundles } from '../../utils/analyticsUtilsBackend';
import '../analytics/ToolUsageChart.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ToolUsageChartBackend = ({ selectedSpace = 'personal', selectedPeriod = '7 derniers jours', onSpaceChange, email = '' }) => {
  const { allEvents, allAggregatedEvents, sessionToolBundles } = useSelector(s => s.sessions || { allEvents: [], allAggregatedEvents: [], sessionToolBundles: [] });
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    // Prefer bundles (robust, pre-merged), fallback to events
    let toolUsage = calculateToolUsagePercentageFromBundles(sessionToolBundles, selectedSpace, selectedPeriod, email);
    if (!toolUsage || toolUsage.length === 0) {
      toolUsage = calculateToolUsagePercentageFromRedux(allEvents, selectedSpace, selectedPeriod, email);
    }
    // Fallback: if nothing found in flat events but we have aggregated events, flatten them on the fly
    if ((!toolUsage || toolUsage.length === 0) && allAggregatedEvents && allAggregatedEvents.length > 0) {
      const flatFromAgg = [];
      const mapSpace = {
        personal: 'personalTools',
        manager: 'managerTools',
        project: 'projectTools'
      };
      const typeMap = {
        personalTools: 'personal_space',
        managerTools: 'manager_space',
        projectTools: 'project_space'
      };
      const wantedArray = mapSpace[selectedSpace] || 'personalTools';
      allAggregatedEvents.forEach(rec => {
        (rec[wantedArray] || []).forEach(t => {
          flatFromAgg.push({
            sessionId: rec.sessionId,
            sessionType: typeMap[wantedArray],
            details: { tool: t.tool || t.name || t },
            timestamp: t.timestamp || rec.datetime,
            email: rec.userEmail || ''
          });
        });
      });
      toolUsage = calculateToolUsagePercentageFromRedux(flatFromAgg, selectedSpace, selectedPeriod, email);
    }
    const labels = toolUsage.map(item => item.tool === '[object Object]' ? 'Unknown Tool' : item.tool);
    const data = toolUsage.map(item => parseFloat(item.percentage));

    // Optional UX: auto-switch to a space that has data if current one is empty
    if (toolUsage.length === 0 && typeof onSpaceChange === 'function') {
      const countFor = (space) => {
        const a = calculateToolUsagePercentageFromBundles(sessionToolBundles, space, selectedPeriod, email).length;
        if (a > 0) return a;
        return calculateToolUsagePercentageFromRedux(allEvents, space, selectedPeriod, email).length;
      };
      const candidates = ['personal', 'project', 'manager'];
      const counts = candidates.map(s => ({ s, c: countFor(s) }));
      let best = counts.reduce((a, b) => (b.c > a.c ? b : a), { s: selectedSpace, c: 0 });
      // If still empty and we have aggregated data, consider it as well
      if (best.c === 0 && allAggregatedEvents && allAggregatedEvents.length > 0) {
        const countFromAgg = (space) => {
          const map = { personal: 'personalTools', project: 'projectTools', manager: 'managerTools' };
          const arr = map[space] || 'personalTools';
          return allAggregatedEvents.reduce((acc, rec) => acc + ((rec[arr] || []).length), 0);
        };
        const aggCounts = candidates.map(s => ({ s, c: countFromAgg(s) }));
        best = aggCounts.reduce((a, b) => (b.c > a.c ? b : a), { s: selectedSpace, c: 0 });
      }
      if (best.c > 0 && best.s !== selectedSpace) onSpaceChange(best.s);
    }

    setChartData({
      labels,
      datasets: [
        {
          label: 'Tool Usage (%)',
          data,
          backgroundColor: '#4CAF50',
          borderColor: '#388E3C',
          borderWidth: 1,
        },
      ],
    });
  }, [allEvents, allAggregatedEvents, sessionToolBundles, selectedSpace, selectedPeriod, email, onSpaceChange]);

  return (
    <div className="tool-usage-chart">
      <h3 className="chart-title">Top fonctionnalités utilisées</h3>

      <div className="filter-container">
        <label htmlFor="space-filter" className="filter-label">Espace :</label>
        <select
          id="space-filter"
          className="filter-dropdown"
          value={selectedSpace}
          onChange={(e) => onSpaceChange(e.target.value)}
        >
          <option value="personal">Espace Personnel</option>
          <option value="project">Espace Projet</option>
          <option value="manager">Espace Manager</option>
        </select>
      </div>

      {chartData && (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (context) => `${context.raw}%`,
                },
              },
              title: {
                display: true,
                text: 'Pourcentage d\'utilisation des outils',
              },
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: 'Outils',
                  color: '#388E3C',
                  font: { size: 14, weight: 'bold' },
                },
                ticks: { color: '#4CAF50' },
              },
              y: {
                title: {
                  display: true,
                  text: 'Pourcentage (%)',
                  color: '#388E3C',
                  font: { size: 14, weight: 'bold' },
                },
                ticks: { color: '#4CAF50' },
                beginAtZero: true,
              },
            },
          }}
        />
      )}
    </div>
  );
};

export default ToolUsageChartBackend;
